/* global globalThis */
import { getAuthorizationToken, getIdentityAssertion } from '@/apps/apis/claudeCodeDashboard/token';

export class CompetitorV2ApiError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'CompetitorV2ApiError';
  }
}

export interface DpQuantity {
  unitCount: number;
  unit: string;
  packCount: number;
}

export interface DpQuantities {
  targetUnitCount: number;
  targetUnit: string;
  targetPackCount: number;
  sourceUnitCount: number;
  sourceUnit: string;
  sourcePackCount: number;
}

export type DpMatchStatus = 'AUTO' | 'NEEDS_REVIEW' | 'APPROVED' | 'REJECTED';

/** One `ss_dp_match` row in the review list. */
export interface DpReviewSummary {
  targetSku: string;
  source: string;
  status: DpMatchStatus;
  productName: string | null;
  brand: string | null;
  reasonCode: string | null;
  updatedAt: string;
}

export interface DpReviewPage {
  items: DpReviewSummary[];
  nextCursor: string | null;
}

/** A stored candidate joined with its current observation; `observation` is null when it is no longer observed. */
export interface DpMatchCandidate {
  sourceProductId: string;
  optionKey: string;
  market: string;
  observationId?: string | null;
  score: number | null;
  note?: string | null;
  observation: {
    productName: string | null;
    brand: string | null;
    optionLabel: string | null;
    unitCount: number | null;
    unit: string | null;
    packCount: number | null;
    listPrice: string | null;
    salePrice: string | null;
    currency: string | null;
    availability: string | null;
    observedAt: string | null;
    sourceUrl: string | null;
  } | null;
}

export interface DpMatch {
  status: DpMatchStatus;
  sourceProductId: string | null;
  optionKey: string | null;
  market: string | null;
  quantities: DpQuantities | null;
  reasonCode: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  updatedAt: string;
  target: {
    sku: string;
    name: string;
    brand: string;
    size: string | null;
    pack: string | null;
    targetQuantity: DpQuantity | null;
    regularPrice: string | null;
    salePrice: string | null;
    currency: string | null;
    /** Storefront USD price; optional so the page still works against a Facade without it. */
    regularPriceUsd?: string | null;
    salePriceUsd?: string | null;
    productUrl: string | null;
    optionLabel: string | null;
  };
  candidates: DpMatchCandidate[];
}

/** The server answers NO_CANDIDATE when no match row exists. */
export type DpMatchDetail = DpMatch | { status: 'NO_CANDIDATE' };

export interface DpMatchDecision {
  decision: 'APPROVED' | 'REJECTED' | 'NEEDS_REVIEW';
  sourceProductId?: string;
  optionKey?: string;
  market?: string;
  quantities?: DpQuantities;
  note?: string;
}

export type DpPriceStatus = 'PROPOSED' | 'HELD' | 'APPROVED' | 'REJECTED' | 'PUBLISHED';

/** One `ss_dp_price` row. */
export interface DpPriceRow {
  targetSku: string;
  status: DpPriceStatus;
  competitorSource: string | null;
  competitorPriceUsd: string | null;
  proposedRegularPrice: string | null;
  proposedSalePrice: string | null;
  previousEffectivePrice: string | null;
  holdReason: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  publishedAt: string | null;
  updatedAt: string;
}

/** A price review row with what staff need to decide it. */
export interface DpPriceReviewItem extends DpPriceRow {
  /** The store price Batch read; null when Batch held the row before reading it. */
  currentRegularSgd: string | null;
  currentSaleSgd: string | null;
  competitorUrl: string | null;
  productUrl: string | null;
}

export interface DpPriceReviewPage {
  items: DpPriceReviewItem[];
  nextCursor: string | null;
}

/** HTTP client for the DP match and price review routes; every call carries the staff session. */
export class CompetitorV2Client {
  private readonly request: typeof fetch;

  public constructor(
    private readonly apiUrl: string,
    request?: typeof fetch,
  ) {
    this.request = request ?? ((input, init) => globalThis.fetch(input, init));
  }

  public listDpReviews(source: string, status: DpMatchStatus | '', cursor?: string, q?: string): Promise<DpReviewPage> {
    return this.get('/competitor/v2/dp/reviews', { source, status, limit: 20, cursor, q: q?.trim() });
  }

  public getDpMatch(targetSku: string, source: string): Promise<DpMatchDetail> {
    return this.get(`/competitor/v2/dp/matches/${encodeURIComponent(targetSku)}/${encodeURIComponent(source)}`);
  }

  public decideDpMatch(targetSku: string, source: string, body: DpMatchDecision): Promise<unknown> {
    return this.send(
      `/competitor/v2/dp/matches/${encodeURIComponent(targetSku)}/${encodeURIComponent(source)}/decision`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  public listDpPriceReviews(
    status: DpPriceStatus | '',
    holdReason: string,
    cursor?: string,
  ): Promise<DpPriceReviewPage> {
    return this.get('/competitor/v2/dp/pricing/reviews', { status, holdReason, limit: 20, cursor });
  }

  private async get<T>(path: string, query: Record<string, string | number | undefined> = {}): Promise<T> {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') {
        search.set(key, String(value));
      }
    }
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 15_000);
    try {
      const qs = search.toString();
      return await this.send<T>(qs ? `${path}?${qs}` : path, {
        method: 'GET',
        signal: controller.signal,
      });
    } catch (error) {
      if (timedOut) {
        throw new CompetitorV2ApiError(408, 'DP_READ_TIMEOUT');
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  private async send<T>(path: string, init: RequestInit): Promise<T> {
    const token = await getAuthorizationToken();
    if (!token && init.method !== 'GET') {
      throw new CompetitorV2ApiError(401, 'A staff session is required for match decisions.');
    }
    const assertion = await getIdentityAssertion();
    const response = await this.request(`${this.apiUrl.replace(/\/$/u, '')}${path}`, {
      ...init,
      credentials: 'omit',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(assertion ? { 'x-identity-assertion': assertion } : {}),
      },
    });
    if (!response.ok) {
      throw new CompetitorV2ApiError(response.status, await CompetitorV2Client.errorMessage(response));
    }
    return (await response.json()) as T;
  }

  private static async errorMessage(response: Response): Promise<string> {
    try {
      const body = (await response.json()) as { message?: unknown };
      return typeof body.message === 'string' ? body.message : `Competitor V2 request failed (${response.status})`;
    } catch {
      return `Competitor V2 request failed (${response.status})`;
    }
  }
}
